/**
 * Shopify custom element section.
 *
 * Liquid renders <figma-event-countdown> and loads this file with `defer`, so the browser
 * upgrades the element and this class owns init / teardown. The `customElements.get` guard
 * is required: the section can be placed more than once, which emits the same asset URL
 * twice, and a tag name can never be redefined once registered.
 */
class FigmaEventCountdown extends HTMLElement {
  connectedCallback() {
    // Moving an upgraded node within the document re-runs connectedCallback; bind once.
    if (this.dataset.countdownReady) {
      return;
    }

    this.dataset.countdownReady = 'true';

    this.valueNodes = new Map();

    this.querySelectorAll('[data-countdown-part]').forEach((node) => {
      this.valueNodes.set(node.dataset.countdownPart, node);
    });

    this.timerNode = this.querySelector('.figma-event-countdown__timer') || this;
    this.endedNode = this.querySelector('[data-countdown-ended]');

    if (!this.valueNodes.size) {
      return;
    }

    this.target = this.parseLocalDate((this.dataset.countdownEnd || '').trim());

    // Unusable end date: flag it, leave the placeholder text alone and never start a timer.
    if (!this.target || Number.isNaN(this.target.getTime())) {
      this.timerNode.classList.add('is-invalid');
      return;
    }

    this.timer = 0;
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.tick();

    // Nothing left to count towards at zero, so never start the interval.
    if (this.remaining <= 0) {
      this.showEndedState();
      return;
    }

    this.timer = window.setInterval(() => this.tick(), 1000);

    // Background tabs get throttled regardless, but stopping outright avoids the wake-up
    // cost and guarantees the display is correct the moment the tab comes back.
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  disconnectedCallback() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = 0;
    }

    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  /**
   * Same contract as the ERGO 360 hero countdown: `YYYY-MM-DD` optionally followed by
   * `HH:MM:SS` (a `/` date separator or a `T` between date and time also works). When a
   * `data-timezone` IANA zone is set, the wall-clock time is interpreted in that zone
   * (e.g. America/Los_Angeles); otherwise it stays in the visitor's local timezone.
   * Anything the pattern does not match falls through to the native parser.
   */
  parseLocalDate(value) {
    const parts = this.parseDateParts(value);

    if (parts) {
      const timeZone = (this.dataset.timezone || '').trim();

      if (timeZone) {
        const ms = this.zonedTimeToMs(parts, timeZone);

        if (ms !== null) {
          return new Date(ms);
        }
      }

      return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    }

    return new Date(value);
  }

  /**
   * Interpret wall-clock parts in `timeZone` (IANA name, e.g. America/Los_Angeles) and
   * return the UTC timestamp. Two offset passes handle DST boundaries. Returns null when
   * the browser rejects the zone name, so callers can fall back to local time.
   */
  zonedTimeToMs(parts, timeZone) {
    const utcGuess = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);

    const zoneOffset = (date) => {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      const map = {};

      dtf.formatToParts(date).forEach((part) => {
        map[part.type] = part.value;
      });

      return Date.UTC(
        Number(map.year),
        Number(map.month) - 1,
        Number(map.day),
        Number(map.hour === '24' ? '0' : map.hour),
        Number(map.minute),
        Number(map.second)
      ) - date.getTime();
    };

    try {
      let ms = utcGuess - zoneOffset(new Date(utcGuess));
      ms = utcGuess - zoneOffset(new Date(ms));
      return ms;
    } catch (error) {
      return null;
    }
  }

  parseDateParts(value) {
    const match = value.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s](\d{1,2})(?::(\d{1,2})(?::(\d{1,2}))?)?)?$/);

    if (!match) {
      return null;
    }

    return {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4] || 0),
      minute: Number(match[5] || 0),
      second: Number(match[6] || 0)
    };
  }

  handleVisibilityChange() {
    if (document.hidden) {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = 0;
      }

      return;
    }

    // Re-sync first: the interval was not running while the tab was hidden.
    this.tick();

    if (this.remaining > 0 && !this.timer) {
      this.timer = window.setInterval(() => this.tick(), 1000);
    }
  }

  tick() {
    this.remaining = Math.max(0, this.target.getTime() - Date.now());

    const totalSeconds = Math.floor(this.remaining / 1000);
    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60
    };

    let changed = false;

    Object.keys(values).forEach((unit) => {
      const node = this.valueNodes.get(unit);

      if (!node) {
        return;
      }

      const next = String(values[unit]).padStart(2, '0');

      if (node.textContent === next) {
        return;
      }

      node.textContent = next;
      changed = true;
    });

    if (this.remaining <= 0) {
      this.showEndedState();

      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = 0;
      }
    }

    this.timerNode.classList.toggle('is-expired', this.remaining === 0);
  }

  showEndedState() {
    if (!this.endedNode || this.endedShown) {
      return;
    }

    const text = (this.dataset.endedText || '').trim();

    if (!text) {
      return;
    }

    this.endedNode.textContent = text;
    this.endedNode.hidden = false;
    this.endedShown = true;
  }
}

if (!customElements.get('figma-event-countdown')) {
  customElements.define('figma-event-countdown', FigmaEventCountdown);
}
